import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CustomerAssignedVoucher } from 'src/app/stores/stores';
import { formatVoucherValue } from 'src/app/shared/utils/VoucherUtils';
import { RadioOption } from 'src/app/shared/radio-group/radio-group.component';

export interface VoucherSelectionDialogData {
  vouchers: CustomerAssignedVoucher[];
  selectedVoucherCode?: string;
  isLoading?: boolean;
  showManageVouchersLink?: boolean;
  currencyIsoCode?: string;
  currencySymbol?: string;
}

export interface VoucherSelectionResult {
  selectedVoucherCode: string;
  action: 'apply' | 'manage';
}

/** RadioOption plus the extra fields the projected voucher card renders.
 *  `label` stays '' — the card text is rendered by the #optionContent
 *  template so it is not run through the translate pipe. */
interface VoucherOption extends RadioOption {
  /** Full display text ("CODE (value)") shown in the card. */
  cardLabel: string;
  /** FontAwesome icon class shown in the card. */
  cardIcon: string;
}

@Component({
  selector: 'app-voucher-selection-dialog',
  templateUrl: './voucher-selection-dialog.component.html',
  styleUrls: ['./voucher-selection-dialog.component.scss'],
})
export class VoucherSelectionDialogComponent implements OnInit {
  voucherForm: FormGroup;
  voucherOptions: VoucherOption[] = [];
  isLoading = false;
  showManageVouchersLink = true;

  constructor(
    public dialogRef: MatDialogRef<VoucherSelectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: VoucherSelectionDialogData,
  ) {
    this.isLoading = data.isLoading || false;
    this.showManageVouchersLink = data.showManageVouchersLink !== false;

    this.voucherForm = new FormGroup({
      selectedVoucher: new FormControl(data.selectedVoucherCode || ''),
    });
  }

  ngOnInit(): void {
    this.updateVoucherOptions();
  }

  updateVoucherOptions(): void {
    this.voucherOptions = this.data.vouchers.map((voucher, i) => ({
      value: voucher.code,
      label: '',
      id: 'voucher-' + i,
      cardLabel: `${voucher.code} (${formatVoucherValue(voucher, {
        currencyIsoCode: this.data.currencyIsoCode,
        currencySymbol: this.data.currencySymbol,
      })})`,
      cardIcon: 'fas fa-tag',
    }));
  }

  onApply(): void {
    const selectedVoucherCode = this.voucherForm.get('selectedVoucher')?.value;
    if (selectedVoucherCode) {
      this.dialogRef.close({
        selectedVoucherCode,
        action: 'apply',
      } as VoucherSelectionResult);
    }
  }

  onManageVouchers(): void {
    this.dialogRef.close({
      selectedVoucherCode: '',
      action: 'manage',
    } as VoucherSelectionResult);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  get selectedVoucherCode(): string {
    return this.voucherForm.get('selectedVoucher')?.value || '';
  }

  get hasSelectedVoucher(): boolean {
    return !!this.selectedVoucherCode;
  }

  // Keyboard navigation support
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.onCancel();
    } else if (event.key === 'Enter' && this.hasSelectedVoucher) {
      this.onApply();
    }
  }
}
